import { type ReactElement, type ReactNode, useEffect, useRef } from "react";

import { cx } from "@/utils/cx";

export interface AnchoredMapPopupProps {
	/** Placement classes from resolvePopupPlacement / POPUP_PLACEMENT_WRAPPER_CLASS. */
	wrapperClassName: string;
	children: ReactNode;
	className?: string;
}

/**
 * Absolute shell that positions a map popup around a 0×0 marker anchor.
 * `wrapperClassName` must be tip-centric (`left-0 top-0` + translate so the
 * caret tip lands on the anchor).
 */
export function AnchoredMapPopup({
	wrapperClassName,
	children,
	className,
}: AnchoredMapPopupProps): ReactElement {
	const rootRef = useRef<HTMLDivElement>(null);

	// Mapbox listens for wheel on the canvas container this popup is mounted
	// inside, so a React onWheel (bound at the React root) runs too late.
	useEffect(() => {
		const root = rootRef.current;
		if (root == null) return;

		const stopWheel = (event: WheelEvent): void => {
			event.stopPropagation();
		};

		root.addEventListener("wheel", stopWheel);
		return () => root.removeEventListener("wheel", stopWheel);
	}, []);

	return (
		<div
			ref={rootRef}
			data-testid="anchored-map-popup"
			className={cx(
				"pointer-events-auto absolute z-10",
				wrapperClassName,
				className,
			)}
		>
			{children}
		</div>
	);
}
